import React, { useCallback, useState, ComponentProps, HTMLAttributes, CSSProperties } from "react";

import classnames from "classnames";
import { StyleProps } from "Utils/styleProps";

import Button from "Cobalt/Button";
import { Collapse } from "Cobalt/Collapse";
import { Table as TableWithStyleProps, Td } from "Common/HTML";
import Icon from "Common/Icon";

import makeBem from "Utils/makeBem";

const bem = makeBem("CobaltTableV2");

export type TextAlign = "left" | "center" | "right";

const propsValidation = (className, componentName) =>
  process.env.NODE_ENV === "development" && className && console.warn(`${componentName} doesn't support 'className' prop.`);

export const Table = ({ className, ...rest }: ComponentProps<typeof TableWithStyleProps>) => {
  propsValidation(className, "Table");
  return <TableWithStyleProps className={bem()} {...rest} />;
};

export const HeaderTR = ({ className, children, ...rest }: HTMLAttributes<HTMLTableRowElement>) => {
  propsValidation(className, "HeaderTR");
  return (
    <tr className={bem("HeaderRow")} {...rest}>
      {children}
    </tr>
  );
};

interface THProps extends HTMLAttributes<HTMLTableCellElement> {
  align?: TextAlign;
  fitContent?: boolean;
  snug?: boolean;
  colSpan?: number;
  width?: string;
}

export const TH = ({ align, fitContent, snug, className, children, ...rest }: THProps) => {
  propsValidation(className, "TH");
  const classes = classnames(bem("HeaderCell"), {
    [`text-${align}`]: align,
    [bem("HeaderCell", "fit-content")]: fitContent,
    [bem("HeaderCell", "snug")]: snug,
  });
  return (
    <th className={classes} {...rest}>
      {children}
    </th>
  );
};

export const SnugTH = ({ children, className, ...rest }: THProps) => {
  propsValidation(className, "SnugTH");
  return (
    <TH fitContent snug {...rest}>
      {children}
    </TH>
  );
};

interface TRProps extends StyleProps {
  bgColor?: string;
  withBorder?: boolean;
  indented?: boolean;
  highlightFirstCell?: boolean;
  className?: string;
  children?: React.ReactNode;
}
// Used to construct the tr that are direct descendents of
// <table> or <tbody>

const BaseTR = ({
  bgColor,
  // "gray" | undefined
  withBorder,
  // true | false
  indented,
  // true | false
  highlightFirstCell,
  // true | false
  className,
  children,
  ...rest
}: TRProps) => {
  const classes = classnames(className, bem("Row"), {
    [bem("Row", `bg-${bgColor}`)]: bgColor,
    [bem("Row", "with-border")]: withBorder,
    [bem("Row", "indented")]: indented,
    [bem("Row", "highlight-first-cell")]: highlightFirstCell,
  });
  return (
    <tr className={classes} {...rest}>
      {children}
    </tr>
  );
};

export const TR = ({ children, className, ...rest }: TRProps) => {
  propsValidation(className, "TR");
  return (
    <BaseTR withBorder highlightFirstCell={false} {...rest}>
      {children}
    </BaseTR>
  );
};
export const NestedTR = ({ children, className, ...rest }: TRProps) => {
  propsValidation(className, "NestedTR");
  return (
    <BaseTR bgColor="gray" indented {...rest}>
      {children}
    </BaseTR>
  );
};
export const SpacerTR = ({ className, ...rest }: TRProps) => {
  propsValidation(className, "SpacerTR");
  return (
    <tr className={bem("SpacerRow")} aria-hidden {...rest}>
      <td className="SpacerTD" colSpan={99} />
    </tr>
  );
};
export const SnugTR = ({ children, className, ...rest }: TRProps) => {
  propsValidation(className, "SnugTR");
  return (
    <BaseTR className={bem("SnugRow")} withBorder highlightFirstCell={false} {...rest}>
      {children}
    </BaseTR>
  );
};

export interface TDProps extends HTMLAttributes<HTMLTableCellElement> {
  align?: TextAlign;
  verticalAlign?: "top" | "middle" | "bottom";
  children?: React.ReactNode;
  className?: string;
  colSpan?: number;
  fitContent?: boolean;
}
const BaseTD = ({ align, verticalAlign = "middle", fitContent, children, className, ...rest }: TDProps) => {
  const classes = classnames(className, bem("Cell"), {
    [`text-${align}`]: align,
    [`align-${verticalAlign}`]: verticalAlign,
    [bem("Cell", "fit-content")]: fitContent,
  });
  return (
    <Td className={classes} {...rest}>
      {children}
    </Td>
  );
};

export const TD = ({ children, className, ...rest }: TDProps) => {
  propsValidation(className, "TD");
  return <BaseTD {...rest}>{children}</BaseTD>;
};
export const EllipsisTD = ({ minWidth = "100px", children, className, style = {}, ...rest }: TDProps & { minWidth?: CSSProperties["minWidth"] }) => {
  propsValidation(className, "EllipsisTD");
  return (
    <BaseTD
      {...rest}
      className={bem("EllipsisTD")}
      style={{
        minWidth,
        ...style,
      }}
      title={typeof children === "string" ? children : undefined}
    >
      {children}
    </BaseTD>
  );
};

/**
 * Use SnugTD when using a Clickable Icon.
 */
export const SnugTD = ({ children, className, ...rest }: TDProps) => {
  propsValidation(className, "SnugTD");
  return (
    <BaseTD fitContent p={0} {...rest}>
      {children}
    </BaseTD>
  );
};
export const FooterTR = ({ children, className, ...rest }: TRProps) => {
  propsValidation(className, "FooterTR");
  return (
    <tr className={bem("FooterRow")} {...rest}>
      {children}
    </tr>
  );
};
export const FooterTD = ({ align, children, className, ...rest }: TDProps & ComponentProps<typeof Td>) => {
  propsValidation(className, "FooterTD");
  const classes = classnames(bem("FooterCell"), {
    [`text-${align}`]: align,
  });
  return (
    <Td className={classes} {...rest}>
      {children}
    </Td>
  );
};

/**
  @note Mobile-Exclusive - Use ToggleTR inside a <CollapseRowsOrganizer /> component.
  The ToggleTR will append a trailing <ToggleTD /> to the table row
  so the user can toggle isOpen state of the parent component.
  ToggleTR is used together with DetailTR to display data.
*/
export const ToggleTR = ({ isOpen, toggle, children, className, ...rest }: TRProps & { isOpen: boolean; toggle: () => void }) => {
  propsValidation(className, "ToggleTR");
  return (
    <BaseTR bgColor={isOpen ? "gray" : undefined} withBorder highlightFirstCell={false} onClick={toggle} {...rest}>
      {children}
      <ToggleTD isOpen={isOpen} />
    </BaseTR>
  );
};

/**
  @note Mobile-Exclusive - Use DetailTR inside a <CollapseBody /> or
  as part of the `rows` prop of a <CollapseRows /> component.
  The DetailTR is used together with the ToggleTR.
  The ToggleTR will append a trailing empty <TD /> component to match
  the number of TDs from the ToggleTR.
*/
export const DetailTR = ({ nested = false, children, className, ...rest }: TRProps & { nested?: boolean }) => {
  propsValidation(className, "DetailTR");
  return (
    <BaseTR indented={nested} bgColor="gray" className={bem("Row", "detail")} {...rest}>
      {children}
      <TD />
    </BaseTR>
  );
};

const ToggleTD = ({ isOpen, ...rest }: TDProps & { isOpen: boolean }) => (
  <SnugTD {...rest}>
    <CollapseToggleButton isOpen={isOpen} />
  </SnugTD>
);

const CollapseToggleButton = ({ isOpen }) => (
  <Button color="ghost" circle size="xs">
    <span className="sr-only">{isOpen ? "Show details." : "Hide details."}</span>
    <Icon icon={isOpen ? "angle-up" : "angle-down"} />
  </Button>
);

/**
  @note Mobile-Exclusive - Only use CollapseBody if the <CollapseRows /> component
  doesn't suit your needs.
  Use CollapseBody inside a <CollapseRowsOrganizer /> component
  to aggregate content that is displayed/hidden.
  It is possible to use many CollapseBody inside the same <CollapseRowsOrganizer />.
*/
export const CollapseBody = ({
  isOpen,
  collapseProps = {},
  children,
}: {
  isOpen: boolean;
  collapseProps?: ComponentProps<typeof Collapse>;
  children: React.ReactNode;
}) => (
  <Collapse {...collapseProps} in={isOpen}>
    <tbody className={bem("CollapseBody")} aria-hidden={!collapseProps.in}>
      {children}
    </tbody>
  </Collapse>
);

/**
 * @note Mobile-Exclusive - Only use CollapseRowsOrganizer if the <CollapseRows /> component
 * doesn't suit your needs.
 * The CollapseRowsOrganizer simply manages the isOpen state for you.
 * Use <ToggleTR /> to control the isOpen state and <CollapseBody /> components to display/hide content.} param0
 */
export const CollapseRowsOrganizer = ({ children }: { children: (props: { isOpen: boolean; toggle: () => void }) => JSX.Element }) => {
  if (typeof children !== "function") {
    throw new Error(`Unsupported 'children' type. Must be a function, like this: ({isOpen, toggle}) => React.Element`);
  }

  const [isOpen, setIsOpen] = useState(false);
  const toggle = useCallback(() => setIsOpen(!isOpen), [isOpen]);
  return children({
    isOpen,
    toggle,
  });
};

/**
 * @note Mobile Exclusive - The CollapseRows is the go-to component
 * for displaying content in Mobile views.
 * @param {React.Fragment} topCells - Fragment containing <TD> components. These TDs are always visible.
 * @param {React.Fragment} rows - Fragment containing
 */
export const CollapseRows = ({ topCells, rows, className }) => {
  propsValidation(className, "CollapseRows");
  return (
    <CollapseRowsOrganizer>
      {({ isOpen, toggle }) => (
        <>
          <tbody>
            <ToggleTR isOpen={isOpen} toggle={toggle}>
              {topCells}
            </ToggleTR>
          </tbody>
          <CollapseBody isOpen={isOpen}>
            {rows}
            <SpacerTR />
          </CollapseBody>
        </>
      )}
    </CollapseRowsOrganizer>
  );
};
